import { Autocomplete, Button, CircularProgress, Grid, InputLabel, Modal, TableCell, TextField, useTheme } from "@mui/material";
import { memo, useState } from "react";
import VendorForm from "./VendorForm";
import { error400Handler, error422Handler, request } from "@/Api";

import { HighlightedIcon, Subtitle } from "@/common/StyledComponents"
import { ProfileListItem } from "@/profiles_common/CommonComponents"
import { ApartmentRounded, PersonRounded, WorkOutlineOutlined } from "@mui/icons-material"
import { FormSection } from "./StyledComponents";
import ApiAutocomplete from "@/common/ApiAutocomplete";
import FilesUploader from "@/common/FilesUploader";
import CustomTable, { CustomRow } from "@/table/CustomTable";
import { emailIsValid, formattedCurrency } from "@/utils";

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { useSelector } from "react-redux";
import { selectHasPermission } from "@/auth/authSlice";
import VendorContracts from "./VendorContracts";
import { XODO_TEMPLATES } from "./constants";
import { useDispatch } from "react-redux";
import { setError } from "@/nav/navSlice";
import FadeoutExpand from "@/common/FadeoutExpand";

export default function VendorPopup({ vendorData, isOpen, close, addVendor, updateVendor }){
    const theme = useTheme()
    const dispatch = useDispatch()
    const [ vendor, setVendor ] = useState(vendorData || {})
    const [ vendorInvited, setVendorInvited ] = useState(false)
    const [ errors, setErrors ] = useState({})
    const [ syncing, setSyncing ] = useState(false)
    const isEdit = vendor.id != null
    const fullVendor = {
        ...vendor, 
        contact_emails: vendor.contacts ? vendor.contacts.map(contact=>contact.email || contact) : [], 
        skill_ids: vendor.skills ? vendor.skills.map(skill=>skill.id) : [],
    }
    const showBankingInfo = useSelector(state => selectHasPermission(state, "edit_vendor_banking_info"))
    
    const putVendor = () => {
        setSyncing(true)
        request.put(`/vendors/${vendor.id}`, fullVendor)
        .then(response=>{
            dispatch(setError({message: "Update vendor success", severity: "success"}))
            updateVendor(vendor.id, response.data)
        })
        .catch(err => {
            error422Handler(err, (parsedError) => {
                setErrors(parsedError)
            })
            error400Handler(err, (parsedError) => {
                dispatch(setError({message: parsedError}))
            })
        })
        .finally(() => {
            setSyncing(false)
        })
    }

    const newVendorWithoutSelectedTemplate = !vendor.id && (!vendor.templates || vendor.templates.length == 0)
    const editingVendor = vendor.id != null

    const postVendor = () => {
        setSyncing(true)
        request.post("/vendors", fullVendor)
        .then(response=>{
            close()
            addVendor(response.data)
            if (vendor.templates) {
                vendor.templates.forEach(template => {
                    request.post("/xodo-use-template", {
                        template_name: template,
                        vendor_id: response.data.id,
                        signers: [{
                            "name": vendor.name || vendor.email,
                            "email": vendor.email,
                            "role": "Client"
                        }]
                    })
                })
            }
        })
        .catch(err => {
            error422Handler(err, (parsedError) => {
                setErrors(parsedError)
            })
            error400Handler(err, (parsedError) => {
                dispatch(setError({message: parsedError}))
            })
        })
        .finally(() => {
            setSyncing(false)
        })
    }

    const saveCallback = isEdit ? putVendor : postVendor

    const sendVendorLink = () => {
        request.post(`/vendor-info-invite/${vendor.id}`)
        .then(response=>{
            if (response.data) {
                setVendorInvited(true)
                setVendor(response.data)
                dispatch(setError({message: "Vendor link sent successfully", severity: "success"}))
            }
        })
        .catch(err => {
            dispatch(setError({message: err.response.data.detail, severity: "error"}))
        })
    }

    const handleVendorExternalSync = () => {
        setSyncing(true)
        request.post(`/vendors/external`, {}, {params: {vendor_id: vendor.id}})
        .then(response => {
            setVendor(response.data)
            updateVendor(vendor.id, response.data)
            dispatch(setError({message: "Vendor data synced to accounting system", severity: "success"}))
        })
        .catch(err => {
            dispatch(setError({message: err.response.data.detail, severity: "error"}))
        })
        .finally(()=>{
            setSyncing(false)
        })
    }

    return (
        <Modal open={isOpen || false} onClose={close}>
            <div style={{background: theme.palette.background.default, width: "62%", float: "right", height: "100vh", overflowY: "auto"}}>
                <div style={{padding: "80px 60px 40px 60px"}}>
                    <Grid container alignItems={"center"} spacing={1}>
                        <Grid item>
                            <HighlightedIcon icon={WorkOutlineOutlined} theme={theme} highlighted />
                        </Grid>
                        <Grid item>
                            <h4>Vendor</h4>
                        </Grid>
                    </Grid>
                    <VendorForm 
                        showBankingInfo={showBankingInfo}
                        vendor={vendor}
                        setVendor={setVendor}
                        children1={<PopupFieldsGroup1 vendor={vendor} />}
                        children2={<PopupFieldsGroup2 vendor={vendor} setVendor={setVendor}/>}
                        children3={<PopupFieldsGroup3 vendor={vendor} />}
                        errors={errors}
                        setErrors={setErrors}
                    />
                </div>
                <div style={{
                    background: theme.palette.background.default,
                    textAlign: "center",
                    position: "sticky",
                    bottom: "0",
                    padding: "30px",
                    alignItems: "center",
                    margin: "auto",
                }}>
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            {vendor.id &&
                            <Grid container spacing={2} alignItems={"center"}>
                                <Grid item>
                                    <Button disabled={vendorInvited} variant="contained" color="secondary" onClick={sendVendorLink}>
                                        {`${vendorInvited ? "Sent" : "Send"} Vendor Link`}
                                    </Button>
                                </Grid>
                                <Grid item>
                                    {syncing ? <CircularProgress /> : <Button variant="contained" color="secondary" onClick={handleVendorExternalSync}>Sync to Quickbooks</Button>}
                                </Grid>
                            </Grid>}
                        </Grid>
                        <Grid item>
                            <Grid container spacing={2} alignItems={"center"}>
                                <Grid item><Button variant="contained" color="secondary" onClick={close}>Close</Button></Grid>
                                <Grid item>
                                    {syncing ? <CircularProgress /> : <Button variant="contained" onClick={saveCallback}>
                                        {(newVendorWithoutSelectedTemplate || editingVendor) ? "Save" : "Send & Save"}
                                    </Button>}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
            </div>
        </Modal>
    )
}

const PopupFieldsGroup3 = ({vendor}) => {
    return (
        <>
            {vendor.id &&
            <FormSection title={"Projects"}>
                <VendorProjectsTable vendor={vendor}/>
            </FormSection>}
        </>
    )
}

const PopupFieldsGroup2 = ({vendor, setVendor}) => {
    const contractTemplates = XODO_TEMPLATES
    const handleAutocompleteChange = (field) => (event, val, toObjectCallback) => {
        if (field == "contacts" && !emailIsValid(val[val.length - 1])) {
            return
        }
        setVendor({...vendor, [field]: val})
    }
    const handleTemplateTrigger = (template) => {
        request.post("/xodo-use-template", {
            template_name: template,
            vendor_id: vendor.id,
            signers: [{
                "name": vendor.name,
                "email": vendor.email,
                "role": "Client"
            }]
        })
        .then(response=>{
            setVendor({...vendor, contracts: [...vendor.contracts, response.data], last_contract: response.data})
        })
    }
    const handleContractDelete = (contract) => {

    }
    const handleTemplatesCheck = (templates) => {
        setVendor({...vendor, templates: templates})
    }
    return (
        <>
            {vendor.business_type == "Company" && 
            <FormSection title={"Contacts"}>
                <Autocomplete
                    autoComplete={false}
                    freeSolo
                    placeholder="+ Add"
                    size="small"
                    silentErrors
                    fullWidth
                    fullOptionData
                    options={[]}
                    value={vendor.contacts ? vendor.contacts.map(contact=>contact.email || contact) : []}
                    onChange={handleAutocompleteChange("contacts")}
                    multiple
                    renderInput={(params) => <TextField placeholder="+ Add" {...params} sx={{"& fieldset": { border: 'none' }}}/>}
                    
                />
            </FormSection>}
            <FormSection title={"Skills"}>
                <ApiAutocomplete
                    freeSolo
                    placeholder="+ Add"
                    size="small"
                    silentErrors
                    fullWidth
                    fullOptionData
                    value={vendor.skills || []}
                    apiRoute={"skills"}
                    fields={["name"]}
                    onChange={handleAutocompleteChange("skills")}
                    multiple
                    inputStyle={{"& fieldset": { border: 'none' }}}
                    isOptionEqualToValue={(option, value)=>{
                        return option.id == value.id
                    }}
                />
            </FormSection>
            <FormSection title={"Contracts"}>
                <FadeoutExpand>
                    <VendorContracts
                        contracts={vendor.contracts}
                        templates={contractTemplates}
                        mode={vendor.id ? "trigger": "check"}
                        onTrigger={handleTemplateTrigger}
                        onDelete={handleContractDelete}
                        onCheck={handleTemplatesCheck}
                    />
                </FadeoutExpand>
            </FormSection>
            {vendor.id &&
            <div style={{paddingTop: 40}}>
                <FilesUploader
                    files={vendor.files || []}
                    modelType="vendor"
                    modelId={vendor.id}
                    title={"Additional Documents"}
                />
            </div>}
        </>
    )
}

const PopupFieldsGroup1 = ({vendor}) => {
    return (
        <>
            <Grid container spacing={1}>
                {vendor.business_type && <Grid item>
                    <ProfileListItem
                        icon={<ApartmentRounded />}
                        text={vendor.business_type}
                    />
                </Grid>}
                {vendor.email && <Grid item>
                    <ProfileListItem
                        icon={<PersonRounded />}
                        text={vendor.email}
                    />
                </Grid>}
                {vendor.external_id && <Grid item>
                    <ProfileListItem
                        secondary="gray"
                        icon={<PersonRounded />}
                        text={`QB: ${vendor.external_id}`}
                    />
                </Grid>}
            </Grid>
        </>
    )
}

const VendorProjectsTable = ({vendor}) => {
    const projects = vendor.projects
    if (!projects || projects.length == 0) {
        return (
            <span style={{color: "gray"}}>Submit an invoice to associate with a project</span>
        )
    }
    return (
        <CustomTable
            headCells={[
                {id: "docket", label: "Docket"}, 
                {id: "project", label: "Project"}, 
                {id: "eps", label: "EPs"}, 
                {id: "status", label: "Status"}, 
                {id: "total", label: "Total"},
                {id: "paid", label: "Paid"},
            ]}
            rows={projects}
            rowComponent={(item) => (
                <CustomRow>
                    <TableCell component="th" scope="row">{item.docket}</TableCell>
                    <TableCell align="left">{item.name}</TableCell>
                    <TableCell align="left">{item.eps_property}</TableCell>
                    <TableCell align="left">{item.status.name}</TableCell>
                    <TableCell align="left">{formattedCurrency(item.bid_quoted)}</TableCell>
                    <TableCell align="left">
                        {item.paid ? 
                        <CheckCircleRoundedIcon color="primary"/> :
                        <HighlightOffRoundedIcon color="inherit" />}
                    </TableCell>
                </CustomRow>
            )}
        />
    )
}